import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const EcommerceDevelopmentDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'custom-ecommerce-platforms',
      title: 'Custom Ecommerce Platforms',
      description: 'Build tailor-made ecommerce platforms designed for the Dubai market. We create scalable, high-performance online stores with custom features, multilingual support, and region-specific payment options to maximize your reach.',
      icon: ICONS.ecommerce,
      features: [
        'Custom Storefront Design & Development',
        'Multilingual & RTL Support (Arabic/English)',
        'Product Catalog & Inventory Management',
        'Advanced Search & Filtering',
        'Mobile-first Responsive Design',
        'Headless Commerce Architecture'
      ]
    },
    {
      id: 'payment-gateway-integration',
      title: 'Payment Gateway Integration',
      description: 'Integrate secure, region-specific payment solutions that your Dubai customers trust. We connect your store with local and international payment gateways including buy-now-pay-later options popular in the UAE market.',
      icon: ICONS.payment,
      features: [
        'Stripe & PayTabs Integration',
        'Tabby & Tamara BNPL Setup',
        'Apple Pay & Google Pay Support',
        'Multi-currency Payment Processing',
        'PCI DSS Compliance & Security',
        'Automated Invoice & Receipt Generation'
      ]
    },
    {
      id: 'marketplace-development',
      title: 'Marketplace Development',
      description: 'Launch multi-vendor marketplace platforms that connect buyers and sellers across the UAE. We build scalable marketplace solutions with vendor management, commission tracking, and automated payouts.',
      icon: ICONS.marketplace,
      features: [
        'Multi-vendor Storefront Management',
        'Vendor Onboarding & Verification',
        'Commission & Payout Automation',
        'Review & Rating Systems',
        'Order Routing & Fulfillment Logic',
        'Vendor Analytics Dashboard'
      ]
    },
    {
      id: 'ecommerce-optimization',
      title: 'Ecommerce Optimization & Analytics',
      description: 'Maximize your online store revenue with data-driven optimization strategies. We analyze user behavior, optimize conversion funnels, and implement A/B testing to continuously improve your ecommerce performance.',
      icon: ICONS.analytics,
      features: [
        'Conversion Rate Optimization (CRO)',
        'A/B Testing & Experimentation',
        'Shopping Cart Abandonment Recovery',
        'SEO for Ecommerce Products',
        'Performance & Speed Optimization',
        'Revenue & Customer Analytics Dashboards'
      ]
    }
  ]

  const tools = [
    { name: 'Shopify', category: 'Platform' },
    { name: 'WooCommerce', category: 'Platform' },
    { name: 'React', category: 'Frontend' },
    { name: 'Next.js', category: 'Framework' },
    { name: 'Stripe', category: 'Payments' },
    { name: 'PayTabs', category: 'Payments' },
    { name: 'Tabby', category: 'BNPL' },
    { name: 'Tamara', category: 'BNPL' },
    { name: 'Magento', category: 'Platform' },
    { name: 'BigCommerce', category: 'Platform' },
    { name: 'Google Analytics', category: 'Analytics' },
    { name: 'Hotjar', category: 'Analytics' }
  ]

  const benefits = [
    {
      title: 'Stores Launched',
      description: 'Successfully delivered online stores for businesses across Dubai and the UAE.',
      stat: '100+'
    },
    {
      title: 'Average Revenue Growth',
      description: 'Our clients see significant revenue increases after launching their ecommerce platforms.',
      stat: '3x'
    },
    {
      title: 'Payment Uptime',
      description: 'Reliable payment processing ensuring your customers can always complete their purchases.',
      stat: '99.9%'
    },
    {
      title: 'Conversion Improvement',
      description: 'Average improvement in conversion rates through our optimization and UX strategies.',
      stat: '45%'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const faqs = [
    {
      question: 'How much does ecommerce development cost in Dubai?',
      answer: 'Ecommerce development costs in Dubai vary by platform and complexity. A Shopify or WooCommerce store with standard features starts from AED 15,000 to AED 40,000. A custom-built ecommerce platform with unique features, multilingual support, and advanced integrations ranges from AED 50,000 to AED 200,000. Multi-vendor marketplace platforms typically cost AED 100,000 to AED 350,000. We provide detailed proposals with fixed milestones so you know the exact investment before starting.'
    },
    {
      question: 'How long does it take to build an ecommerce website in Dubai?',
      answer: 'A standard Shopify or WooCommerce store can be launched in 3 to 5 weeks. A custom ecommerce platform with advanced features takes 6 to 12 weeks. Multi-vendor marketplace development typically requires 10 to 16 weeks. Timelines depend on the number of products, custom features, payment integrations, and multilingual requirements. We use agile sprints so you can see progress weekly and launch iteratively.'
    },
    {
      question: 'Which ecommerce platform is best for Dubai businesses?',
      answer: 'The best platform depends on your business needs. Shopify is ideal for businesses wanting a quick launch with minimal technical complexity and works well for stores with up to a few thousand products. WooCommerce suits businesses needing more customization and already using WordPress. For large-scale operations with complex requirements, custom-built solutions using React with headless commerce architectures offer the most flexibility. We evaluate your specific needs before recommending a platform.'
    },
    {
      question: 'Which payment gateways work in the UAE?',
      answer: 'We integrate all major payment gateways available in the UAE including Stripe, PayTabs, Checkout.com, and Network International for credit and debit card processing. For buy-now-pay-later options popular with UAE shoppers, we integrate Tabby and Tamara. We also support Apple Pay, Google Pay, Samsung Pay, and bank transfer options. All integrations are PCI DSS compliant to ensure secure payment processing for your customers.'
    },
    {
      question: 'Can you build a multilingual ecommerce store with Arabic support?',
      answer: 'Yes, we build fully multilingual ecommerce stores with proper Arabic RTL (right-to-left) support. This includes bilingual product catalogs, Arabic and English checkout flows, RTL-optimized responsive design, multilingual SEO optimization, and currency support for AED and other currencies. Our Arabic implementations go beyond simple translation to ensure the entire user experience feels native for Arabic-speaking customers.'
    },
    {
      question: 'Do you provide ecommerce SEO and marketing services?',
      answer: 'Yes, we provide comprehensive ecommerce SEO including product page optimization, category page structure, schema markup for products and reviews, site speed optimization, and technical SEO audits. We also implement conversion rate optimization (CRO) strategies including A/B testing, shopping cart abandonment recovery, and user behavior analytics. Our goal is to drive organic traffic and maximize your conversion rates.'
    },
    {
      question: 'Can you build a multi-vendor marketplace like Noon or Amazon?',
      answer: 'Yes, we develop multi-vendor marketplace platforms with features including vendor onboarding and verification, individual vendor storefronts, commission tracking and automated payouts, order routing and fulfillment logic, review and rating systems, vendor analytics dashboards, and admin management panels. We build marketplace platforms that can scale from a handful of vendors to thousands as your platform grows.'
    },
    {
      question: 'Do you offer ongoing support after the ecommerce store launches?',
      answer: 'Yes, we offer flexible post-launch support packages starting from AED 3,000 per month. This includes performance monitoring, security updates and patching, bug fixes, feature enhancements, payment gateway maintenance, and monthly analytics reviews. We also provide emergency support for critical issues with guaranteed response times. Many of our Dubai ecommerce clients maintain ongoing support agreements to ensure their stores run smoothly.'
    }
  ]

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'Ecommerce Development Dubai', path: '/ecommerce-development-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Ecommerce Web Development Dubai | Online Store & Shopping Platform UAE"
        description="Custom ecommerce web development in Dubai. We build online stores, marketplace platforms, and shopping solutions with secure payment integration. Launch your ecommerce business with Demirix."
        keywords={[
          'ecommerce web development dubai',
          'ecommerce development dubai',
          'online store development dubai',
          'ecommerce website dubai',
          'shopify development dubai',
          'woocommerce development uae',
          'online shopping platform dubai',
          'marketplace development dubai',
          'payment gateway integration uae',
          'ecommerce agency dubai',
          'custom ecommerce solutions uae',
          'ecommerce optimization dubai'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              Ecommerce Development
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Ecommerce Web Development in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Custom ecommerce solutions for Dubai businesses, from online stores to marketplace
              platforms with secure payment integration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule Ecommerce Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-semibold text-white mb-1">
                  {benefit.title}
                </div>
                <div className="text-primary-100 text-sm">
                  {benefit.description}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Ecommerce Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                End-to-end ecommerce solutions designed for Dubai businesses looking to sell
                online and grow their digital revenue.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Tools & Technologies We Use
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We leverage industry-leading ecommerce platforms and technologies to build high-converting online stores.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about ecommerce development in Dubai answered by our team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link to other services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Explore our other specialized technology services for Dubai businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dubai-services"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Dubai Services
              </Link>
              <Link
                to="/software-development-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Software Development
              </Link>
              <Link
                to="/it-companies-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                IT Companies Dubai
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Launch Your Online Store?"
        description="Let's discuss how we can build a high-converting ecommerce platform that helps your Dubai business sell online and grow revenue."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default EcommerceDevelopmentDubai
